import React, { createContext, useContext, useState, useEffect } from "react";

export type Mode = "business" | "private";

const MODE_STORAGE_KEY = "zettl:mode";

interface ModeContextType {
  mode: Mode;
  setMode: (mode: Mode) => void;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

export function ModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<Mode>(() => {
    // Initialize from localStorage or default to "business"
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(MODE_STORAGE_KEY);
      if (stored === "business" || stored === "private") {
        return stored as Mode;
      }
    }
    return "business";
  });

  const setMode = (newMode: Mode) => {
    setModeState(newMode);
    if (typeof window !== "undefined") {
      localStorage.setItem(MODE_STORAGE_KEY, newMode);
    }
  };

  // Sync with localStorage changes (e.g., from other tabs)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === MODE_STORAGE_KEY && e.newValue) {
        if (e.newValue === "business" || e.newValue === "private") {
          setModeState(e.newValue as Mode);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return <ModeContext.Provider value={{ mode, setMode }}>{children}</ModeContext.Provider>;
}

export function useMode() {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error("useMode must be used within a ModeProvider");
  }
  return context;
}

/** Mode for UI (e.g. Button) when the tree may omit ModeProvider (e.g. error fallbacks). */
export function useModeForShell(): Mode {
  const context = useContext(ModeContext);
  return context?.mode ?? "business";
}
